// Everyone's badge rank, for admins. A member only ever sees their own rank, on
// their profile; this is the whole table, worked out by the same code on the
// backend (GET /api/badges/admin/ranking). A member's name opens their shelf.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeArt } from "../art";
import { fmtInt, fmtPoints, tierLabel } from "../collection/badgeFormat";
import { useGetAdminBadgeRankingQuery } from "./adminBadgesApi";
import {
  defaultRankingDir,
  rankAbove,
  rankChangeView,
  rankingRows,
  sortRankingRows,
  type RankingSortKey,
  type SortDir,
} from "./adminModel";
import type { AdminRankingRow } from "./adminTypes";

type Column =
  | { key: RankingSortKey; label: string; numeric: boolean; sortable: true; title?: string; sticky?: boolean }
  | { key: string; label: string; numeric: boolean; sortable: false; title?: string; sticky?: boolean };

const COLUMNS: Column[] = [
  { key: "rank", label: "Rank", numeric: true, sortable: true },
  { key: "change", label: "Change", numeric: false, sortable: false, title: "Since the rank a week ago" },
  // stays in view while a phone scrolls the table sideways
  { key: "member", label: "Member", numeric: false, sortable: false, sticky: true },
  { key: "score", label: "Score", numeric: true, sortable: true },
  { key: "distinct", label: "Badges", numeric: true, sortable: false, title: "Different badges held" },
  { key: "copies", label: "Copies", numeric: true, sortable: true },
  { key: "unseen", label: "Unseen", numeric: true, sortable: false, title: "Copies not collected yet" },
  { key: "top", label: "Rarest", numeric: false, sortable: false, title: "Their three rarest badges" },
];

function hhmm(iso: string | undefined): string | null {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function ChangeCell({ change }: { change: number | null }) {
  const v = rankChangeView(change);
  const arrow = v.tone === "up" ? "▲" : v.tone === "down" ? "▼" : v.tone === "flat" ? "=" : "";
  return (
    <td className={`badm-rank__move badm-rank__move--${v.tone}`}>
      <span aria-label={v.label} title={v.label}>
        {arrow && <span aria-hidden="true">{arrow} </span>}
        <span aria-hidden="true">{v.text}</span>
      </span>
    </td>
  );
}

function RankRow({ row, above }: { row: AdminRankingRow; above: number | null }) {
  return (
    <tr data-username={row.username}>
      <td className="badm-num badm-rank__rank">#{fmtInt(row.rank)}</td>
      <ChangeCell change={row.change} />
      <td className="badm-rank__sticky">
        <Link className="badm-rank__member" to={`/badges/u/${encodeURIComponent(row.username)}`}>
          {row.username}
        </Link>
      </td>
      <td className="badm-num">
        <span className="badm-rank__score">{fmtPoints(row.score)}</span>
        {row.pointsToNext !== null && above !== null && (
          <span className="badm-rank__gap">
            {fmtPoints(row.pointsToNext)} to #{fmtInt(above)}
          </span>
        )}
      </td>
      <td className="badm-num">{fmtInt(row.distinct)}</td>
      <td className="badm-num">{fmtInt(row.copies)}</td>
      <td className="badm-num">
        {row.unseen ? (
          <span className="badm-mark badm-mark--unseen">{fmtInt(row.unseen)}</span>
        ) : (
          <span className="badm-zero">0</span>
        )}
      </td>
      <td>
        <span className="badm-rank__top">
          {row.topBadges.map((b) => (
            <span key={b.code} className="badm-rank__mini" title={`${b.name}, ${tierLabel(b.tier)}${b.count > 1 ? `, x${b.count}` : ""}`}>
              <BadgeArt
                category={b.category}
                tier={b.tier}
                number={b.number}
                count={b.count}
                code={b.code}
                name={b.name}
                size={30}
                variant="mini"
              />
            </span>
          ))}
        </span>
      </td>
    </tr>
  );
}

const RankingPanel: React.FC = () => {
  const { data, isLoading, isError, isFetching, refetch } = useGetAdminBadgeRankingQuery();
  const [sort, setSort] = useState<{ key: RankingSortKey; dir: SortDir }>({ key: "rank", dir: "asc" });

  const all = useMemo(() => rankingRows(data), [data]);
  const rows = useMemo(() => sortRankingRows(all, sort.key, sort.dir), [all, sort]);
  const above = useMemo(() => rankAbove(all), [all]);
  const at = hhmm(data?.generatedAt);

  const sortBy = (key: RankingSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: defaultRankingDir(key) }));

  let body: React.ReactNode;
  if (isLoading) {
    body = <p className="badm-muted">Loading the ranking</p>;
  } else if (isError) {
    body = (
      <div className="badm-inline-row">
        <p className="badm-muted">Could not load the ranking.</p>
        <button type="button" className="badm-btn badm-btn--sm" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (!all.length) {
    body = <p className="badm-muted">Nobody holds a badge yet, so nobody is ranked.</p>;
  } else {
    body = (
      <>
        <div className="badm-rank__intro">
          <p className="badm-lead">
            {fmtInt(data?.members ?? all.length)} {(data?.members ?? all.length) === 1 ? "member holds" : "members hold"} at
            least one badge. Members only see their own rank, on their profile. Change is against their rank a week
            ago{at ? `; worked out at ${at}` : ""}.
          </p>
          <button type="button" className="badm-btn badm-btn--sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing" : "Refresh"}
          </button>
        </div>
        <div className="badm-table-wrap badm-table-wrap--wide">
          <table className="badm-table badm-rank" aria-label="Badge ranking">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={
                      [c.numeric ? "badm-num" : "", c.sticky ? "badm-rank__sticky" : ""].filter(Boolean).join(" ") ||
                      undefined
                    }
                    title={c.title}
                    aria-sort={
                      !c.sortable
                        ? undefined
                        : sort.key === c.key
                          ? sort.dir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                    }
                  >
                    {c.sortable ? (
                      <button type="button" className="badm-sort" onClick={() => sortBy(c.key)}>
                        {c.label}
                        <span className="badm-sort__arrow" aria-hidden="true">
                          {sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                        </span>
                      </button>
                    ) : (
                      <span className="badm-th">{c.label}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <RankRow key={row.userId} row={row} above={above.get(row.userId) ?? null} />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <section className="badm-panel" aria-labelledby="badm-rank-title" data-testid="badm-ranking">
      <header className="badm-panel__head" style={{ background: "#d9ccff" }}>
        <h2 id="badm-rank-title" className="badm-panel__title">
          Ranking
        </h2>
        <span className="badm-panel__aside">everyone, admins only</span>
      </header>
      <div className="badm-panel__body">{body}</div>
    </section>
  );
};

export default RankingPanel;
